import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ArrowLeft, Copy, Pencil, QrCode, Plus } from 'lucide-react';
import { api, ErroApi } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { FormAtivo, type DadosAtivo } from './FormAtivo';
import { Etiqueta } from './Etiqueta';
import { NOME_EVENTO, NOME_NIVEL, NOME_PROPRIEDADE, NOME_STATUS_ATIVO, dataBR, dinheiro, type Nivel } from './comum';

interface Detalhe extends DadosAtivo {
  id: string; versao: number; categoria: string; local: string; cliente: string; cliente_id: string; qr_code: string | null; origem: string;
  pai_apelido: string | null; agrupador: boolean; nivel_efetivo: Nivel; nivel_categoria: Nivel; filhos: { id: string; apelido: string; status: string; categoria: string }[];
  arvore: { id: string; apelido: string; status: string; categoria: string; nivel: number; ativo_pai_id: string; fabricante: string | null; modelo: string | null }[];
  eventos: { tipo: string; detalhe: Record<string, string>; em: string; usuario: string | null }[];
  os: { id: string; numero: string; status: string; criado_em: string; acao: string; diagnostico: string | null }[];
}

export function DetalheAtivo() {
  const { id } = useParams();
  const navegar = useNavigate();
  const { usuario } = useSessao();
  const [a, setA] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [modal, setModal] = useState<'editar' | 'etiqueta' | 'componente' | 'duplicar' | null>(null);
  const podeEditar = pode(usuario, 'cadastro.editar');

  const carregar = useCallback(() => {
    api<Detalhe>(`/ativos/${id}`).then(setA).catch((e) => setErro(e instanceof ErroApi && e.status === 404 ? 'Equipamento não encontrado.' : e.message));
  }, [id]);
  useEffect(carregar, [carregar]);

  async function abrirEtiqueta() {
    if (a && !a.qr_code) { const r = await api<{ qr_code: string }>(`/ativos/${a.id}/qr`, { metodo: 'POST' }); setA({ ...a, qr_code: r.qr_code }); }
    setModal('etiqueta');
  }

  if (erro) return <><Link to="/ativos" className="voltar"><ArrowLeft size={16} /> Parque instalado</Link><p className="alerta">{erro}</p></>;
  if (!a) return <p className="nota">Carregando…</p>;

  const dados: [string, string][] = [
    ['Fabricante / modelo', [a.fabricante, a.modelo].filter(Boolean).join(' ') || '—'],
    ['Nº de série', a.numero_serie || '—'], ['IP', a.ip || '—'], ['MAC', a.mac || '—'],
    ['Instalado em', dataBR(a.instalado_em)], ['Garantia até', dataBR(a.garantia_ate)],
    ['De quem é', NOME_PROPRIEDADE[a.propriedade] ?? a.propriedade], ['Valor de reposição', dinheiro(a.valor_reposicao)],
    ['Etiqueta', a.qr_code ?? 'não gerada'],
    ...(a.comprimento_m ? [['Comprimento', `${a.comprimento_m} m`] as [string, string]] : []),
    ['Nível', `${NOME_NIVEL[a.nivel_efetivo]}${a.nivel ? ' (deste equipamento)' : ' (da categoria)'}`],
    ...(Number(a.quantidade ?? 1) > 1 ? [['Quantidade', String(a.quantidade)] as [string, string]] : []),
  ];
  const mapa = a.latitude != null
    ? <p className="nota"><a href={`https://www.google.com/maps?q=${a.latitude},${a.longitude}`} target="_blank" rel="noreferrer">Ver no mapa</a> · fotos dos itens daqui mostram a distância a este ponto</p>
    : null;
  const garantiaVencida = a.garantia_ate && a.garantia_ate < new Date().toISOString().slice(0, 10);

  return (
    <>
      <Link to="/ativos" className="voltar"><ArrowLeft size={16} aria-hidden /> Parque instalado</Link>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">{a.categoria}</span>
          <h1 className="titulo">{a.apelido}</h1>
          <div className="meta">
            <span className={`status ativo-${a.status}`}>{NOME_STATUS_ATIVO[a.status]}</span>
            <Link to={`/clientes/${a.cliente_id}`}>{a.cliente}</Link><span>{a.local}</span>
            {a.pai_apelido && <span>parte de <Link to={`/ativos/${a.ativo_pai_id}`}>{a.pai_apelido}</Link></span>}
          </div>
        </div>
        <div className="botoes">
          {<button className="btn-fantasma" onClick={abrirEtiqueta} disabled={!podeEditar && !a.qr_code}><QrCode size={16} aria-hidden /> Etiqueta</button>}
          {podeEditar && <button className="btn-fantasma" onClick={() => setModal('duplicar')}><Copy size={16} aria-hidden /> Duplicar</button>}
          {podeEditar && <button className="btn-fantasma" onClick={() => setModal('editar')}><Pencil size={16} aria-hidden /> Editar</button>}
        </div>
      </div>

      <div className="grade-ficha">
        <section className="cartao ficha">
          {a.agrupador ? <>
            <h2>Via / setor</h2>
            <p className="nota">Agrupa o que fica no mesmo lugar (portaria, DG, uma via, o perímetro, as ruas de dentro). O sistema (CFTV, cerca, acesso) vem da categoria de cada equipamento. Não é um equipamento: não tem modelo, série, IP nem etiqueta. Use <strong>Duplicar</strong> para criar outra via igual com tudo dentro.</p>
          </> : <>
            <h2>Dados técnicos</h2>
            <dl>{dados.map(([k, v]) => <div key={k}><dt>{k}</dt><dd className={k === 'Garantia até' && garantiaVencida ? 'vencido' : ''}>{v}</dd></div>)}</dl>
          </>}
          {mapa}
          {a.observacoes && <p className="observacoes">{a.observacoes}</p>}
        </section>
        <section className="cartao ficha">
          <div className="secao-topo"><h2>{a.agrupador ? 'Dentro desta via' : 'Componentes'} <span className="contador">{a.arvore.length}</span></h2>
            {podeEditar && <button className="btn-fantasma" onClick={() => setModal('componente')}><Plus size={16} aria-hidden /> {a.agrupador ? 'Equipamento' : 'Componente'}</button>}</div>
          {a.arvore.length === 0 ? <p className="nota">{a.agrupador ? 'Nada pendurado ainda. Comece pelos portões e pendure motor e sensores em cada um.' : 'Nenhum componente pendurado (ex.: placa de comando, fonte, bateria).'}</p> : (
            <ul className="arvore-ativos">{a.arvore.map((f) => (
              <li key={f.id} data-nivel={Math.min(f.nivel, 4)} style={{ ['--nivel' as string]: Math.min(f.nivel, 4) }}>
                <span className="nome"><Link to={`/ativos/${f.id}`}>{f.apelido}</Link>{(f.fabricante || f.modelo) && <small>{[f.fabricante, f.modelo].filter(Boolean).join(' ')}</small>}</span>
                <span className="cat">{f.categoria}</span><span className={`status ativo-${f.status}`}>{NOME_STATUS_ATIVO[f.status]}</span></li>))}</ul>
          )}
        </section>
      </div>

      <section className="secao">
        <h2>Ordens de serviço <span className="contador">{a.os.length}</span></h2>
        {a.os.length === 0 ? <p className="nota">Nenhuma OS registrou atendimento neste equipamento ainda. Quando a tela de OS estiver pronta, cada atendimento aparece aqui.</p> : (
          <ul className="lista-simples">{a.os.map((o) => <li key={o.id}><strong>{o.numero}</strong><span>{o.acao}</span><span>{dataBR(o.criado_em)}</span></li>)}</ul>
        )}
      </section>

      <section className="secao">
        <h2>Histórico</h2>
        <ol className="linha-tempo">{a.eventos.map((e, i) => (
          <li key={i}><time>{new Date(e.em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' })}</time>
            <strong>{NOME_EVENTO[e.tipo] ?? e.tipo}</strong>
            {e.tipo === 'status' && <span>{NOME_STATUS_ATIVO[e.detalhe.de!]} → {NOME_STATUS_ATIVO[e.detalhe.para!]}</span>}
            {e.usuario && <small>por {e.usuario}</small>}</li>))}
        </ol>
      </section>

      <Modal titulo="Editar equipamento" aberto={modal === 'editar'} aoFechar={() => setModal(null)}>
        <FormAtivo inicial={a} aoSalvar={() => { setModal(null); carregar(); }} aoCancelar={() => setModal(null)} />
      </Modal>
      <Modal titulo="Novo componente" aberto={modal === 'componente'} aoFechar={() => setModal(null)}>
        <FormAtivoComPai pai={a} aoSalvar={(nid) => { setModal(null); navegar(`/ativos/${nid}`); }} aoCancelar={() => setModal(null)} />
      </Modal>
      <Modal titulo={`Duplicar “${a.apelido}”`} aberto={modal === 'duplicar'} aoFechar={() => setModal(null)}>
        <FormDuplicar a={a} aoCancelar={() => setModal(null)}
          aoDuplicar={(raizes) => { setModal(null); if (raizes.length === 1) navegar(`/ativos/${raizes[0]}`); else if (a.ativo_pai_id) navegar(`/ativos/${a.ativo_pai_id}`); else carregar(); }} />
      </Modal>
      <Modal titulo={a.agrupador ? 'Etiqueta da via / setor' : 'Etiqueta do equipamento'} aberto={modal === 'etiqueta'} aoFechar={() => setModal(null)}>
        {a.qr_code && <Etiqueta codigo={a.qr_code} tipo={!a.agrupador ? 'equipamento' : /via/i.test(a.categoria) ? 'via' : 'setor'} titulo={a.apelido} sub={`${a.cliente} · ${a.local}`} />}
      </Modal>
    </>
  );
}

function FormAtivoComPai({ pai, aoSalvar, aoCancelar }: { pai: Detalhe; aoSalvar: (id: string) => void; aoCancelar: () => void }) {
  return <FormAtivo localFixo={{ id: pai.local_id, nome: pai.local, clienteId: pai.cliente_id, cliente: pai.cliente }}
    inicial={{ local_id: pai.local_id, categoria_id: '', fabricante: '', modelo: '', apelido: '', numero_serie: '', ip: '', mac: '', instalado_em: '', garantia_ate: '',
      propriedade: pai.propriedade, valor_reposicao: '', ativo_pai_id: pai.id, status: 'em_operacao', observacoes: '' }}
    aoSalvar={aoSalvar} aoCancelar={aoCancelar} />;
}

/** Duplicar: N cópias no mesmo lugar (ex.: sensores iguais) ou a via inteira com tudo dentro (Morador → Visitante). */
function FormDuplicar({ a, aoDuplicar, aoCancelar }: { a: Detalhe; aoDuplicar: (raizes: string[]) => void; aoCancelar: () => void }) {
  const [qtd, setQtd] = useState('1');
  const [comComp, setComComp] = useState(true);
  const [de, setDe] = useState('');
  const [para, setPara] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const n = Math.max(1, Math.min(50, Number(qtd) || 1));
  const total = n * (1 + (comComp ? a.arvore.length : 0));
  const troca = (x: string) => (de.trim() ? x.replace(new RegExp(de.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), para.trim()).replace(/\s{2,}/g, ' ').trim() : x);
  const exemplo = de.trim() ? `${troca(a.apelido)}${n > 1 ? ' 1' : ''}` : `${a.apelido} (cópia${n > 1 ? ' 1' : ''})`;

  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      const r = await api<{ raizes: string[] }>(`/ativos/${a.id}/duplicar`, { metodo: 'POST', corpo: {
        quantidade: n, com_componentes: comComp, ...(de.trim() ? { trocar_de: de.trim(), trocar_para: para.trim() } : {}) } });
      aoDuplicar(r.raizes);
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível duplicar.'); }
    finally { setSalvando(false); }
  }
  return (
    <form className="form" onSubmit={enviar}>
      <p className="nota">Copia categoria, modelo, parque, {a.pai_apelido ? <>o pai (<strong>{a.pai_apelido}</strong>), </> : ''}dono e observações. <strong>Não copia</strong> nº de série, IP, MAC nem etiqueta — são de cada unidade; preencha depois em cada cópia.</p>
      <div className="linha-2">
        <label>Quantas cópias<input type="number" min={1} max={50} value={qtd} onChange={(e) => setQtd(e.target.value)} /></label>
        {a.arvore.length > 0 && <label className="check"><input type="checkbox" checked={comComp} onChange={(e) => setComComp(e.target.checked)} /> Levar o que está dentro ({a.arvore.length})</label>}
      </div>
      <div className="linha-2">
        <label>Trocar no nome (opcional)<input maxLength={60} value={de} onChange={(e) => setDe(e.target.value)} placeholder="Ex.: Morador" /></label>
        <label>por<input maxLength={60} value={para} onChange={(e) => setPara(e.target.value)} placeholder="Ex.: Visitante" disabled={!de.trim()} /></label>
      </div>
      <p className="nota">Vai criar <strong>{total}</strong> registro{total === 1 ? '' : 's'}. Nome da cópia: <strong>{exemplo}</strong>{de.trim() && comComp && a.arvore[0] ? <> · ex. dentro: <strong>{troca(a.arvore[0].apelido)}</strong></> : null}</p>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || total > 300}>{salvando ? 'Duplicando…' : 'Duplicar'}</button>
      </div>
    </form>
  );
}
